/**
 * ConditionPicker — 4 horizontal chips for condition grade selection.
 *
 * Colour is the SELECTION signal, not the label. All four chips used to be
 * drawn in their own grade colour while unselected — green, blue, orange, red
 * side by side — so the row shouted four warnings at once and nothing said
 * which grade was actually chosen. Blue and green appear nowhere else in the
 * app, and the same control on the listing wizard is a neutral outline.
 *
 * Unselected: neutral outline, exactly like `components/ui/Chip` variant
 * "outlined". Selected: the grade colour fills the chip.
 */
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Pressable } from '@/components/ui/Pressable'
import { selection } from '@/lib/utils/haptics'
import { FONTS, SPACING, RADIUS, COLORS } from '@/lib/brand'
import { useSemanticColors, INTERACTION } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import type { ConditionGrade } from '@/lib/types/inspection.types'

interface ConditionPickerProps {
  label: string
  value: ConditionGrade | undefined
  onChange: (grade: ConditionGrade) => void
  testIDPrefix?: string
}

const GRADE_CONFIG: { grade: ConditionGrade; color: string; i18nKey: string }[] = [
  { grade: 'excellent', color: COLORS.condition.excellent, i18nKey: 'checkin.condition_excellent' },
  { grade: 'good', color: COLORS.condition.good, i18nKey: 'checkin.condition_good' },
  { grade: 'fair', color: COLORS.condition.fair, i18nKey: 'checkin.condition_fair' },
  { grade: 'poor', color: COLORS.condition.poor, i18nKey: 'checkin.condition_poor' },
]

export function ConditionPicker({
  label,
  value,
  onChange,
  testIDPrefix = 'checkin',
}: ConditionPickerProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()

  return (
    <View style={{ gap: SPACING.sm }}>
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
        {label}
      </Text>
      <View style={{ flexDirection: 'row', gap: SPACING.sm, flexWrap: 'wrap' }}>
        {GRADE_CONFIG.map(({ grade, color, i18nKey }) => {
          const isSelected = value === grade
          return (
            <Pressable
              key={grade}
              onPress={() => {
                void selection()
                onChange(grade)
              }}
              accessibilityRole="radio"
              accessibilityLabel={t(i18nKey)}
              accessibilityState={{ checked: isSelected }}
              testID={`${testIDPrefix}.condition-${grade}`}
              style={{
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.sm,
                borderRadius: RADIUS.full,
                // Same border weight in both states so selecting a grade does
                // not resize the chip under the finger.
                borderWidth: 1,
                borderColor: isSelected ? color : colors.border,
                backgroundColor: isSelected ? color : 'transparent',
                minHeight: INTERACTION.minTouchTarget,
                justifyContent: 'center',
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  // White on the filled grade colour; otherwise the normal
                  // label colour of any unselected control.
                  color: isSelected ? '#FFFFFF' : colors.textPrimary,
                }}
                numberOfLines={1}
              >
                {t(i18nKey)}
              </Text>
            </Pressable>
          )
        })}
      </View>
    </View>
  )
}
